import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _PropTypes from 'prop-types';
import * as React from 'react';
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { KEYS } from '../../constants/keys.js';
import { getTree } from '../../utils/tree.js';
import { TreeBase } from './components/TreeBase.js';
import { TreeDragNode } from './components/TreeDragNode.js';
import { TreeNode } from './components/TreeNode/TreeNode.js';
import { isTreeDropNodeValid } from './utils.js';
import { jsxs, jsx } from 'react/jsx-runtime';

var dnd = {
  useDrag: useDrag,
  useDragLayer: useDragLayer,
  useDrop: useDrop
};

var Tree = function Tree(_ref) {
  var canOrder = _ref.canOrder,
      _ref$canDrag = _ref.canDrag,
      canDrag = _ref$canDrag === void 0 ? function () {
    return true;
  } : _ref$canDrag,
      _ref$canDrop = _ref.canDrop,
      canDrop = _ref$canDrop === void 0 ? function () {
    return true;
  } : _ref$canDrop,
      filterBy = _ref.filterBy,
      focusIndex = _ref.focusIndex,
      getInitialHiddenIds = _ref.getInitialHiddenIds,
      nodeById = _ref.nodeById,
      onChange = _ref.onChange,
      _onCollapse = _ref.onCollapse,
      onDragStart = _ref.onDragStart,
      _onExpand = _ref.onExpand,
      onFocusIndexChange = _ref.onFocusIndexChange,
      orderBy = _ref.orderBy,
      renderButtons = _ref.renderButtons,
      renderIcon = _ref.renderIcon,
      renderLabel = _ref.renderLabel,
      variant = _ref.variant;

  var _React$useState = React.useState(function () {
    return getInitialHiddenIds ? getInitialHiddenIds(getTree({
      filterBy: filterBy,
      nodeById: nodeById,
      orderBy: orderBy
    }).context) : new Set();
  }),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      hiddenIds = _React$useState2[0],
      setHiddenIds = _React$useState2[1];

  var _React$useState3 = React.useState(),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      a11yDragId = _React$useState4[0],
      setA11yDragId = _React$useState4[1];

  return /*#__PURE__*/jsxs(DndProvider, {
    backend: HTML5Backend,
    children: [/*#__PURE__*/jsx(TreeDragNode, {
      renderLabel: renderLabel
    }), /*#__PURE__*/jsx(TreeBase, {
      "aria-multiselectable": (variant === null || variant === void 0 ? void 0 : variant.type) === "multiselect",
      "data-testid": "mntl-tree-base",
      filterBy: filterBy,
      focusIndex: focusIndex,
      onExpand: _onExpand,
      onFocusIndexChange: onFocusIndexChange,
      getAriaDropEffect: onChange && a11yDragId ? function (options) {
        return isTreeDropNodeValid({
          canDrop: canDrop,
          source: {
            context: options.context.treeContext.getNodeContext(a11yDragId),
            node: nodeById[a11yDragId]
          },
          target: options
        }) ? "move" : void 0;
      } : void 0,
      getAriaGrabbed: onChange ? function (props) {
        if (a11yDragId === props.context.id) {
          return true;
        } else if (canDrag(props)) {
          return "supported";
        } else {
          return false;
        }
      } : void 0,
      getAriaSelected: function getAriaSelected(options) {
        if ((variant === null || variant === void 0 ? void 0 : variant.type) === "multiselect") {
          return !!variant.renderCheckbox(options).checked;
        } else if ((variant === null || variant === void 0 ? void 0 : variant.type) === "select" && variant.isSelected(options)) {
          return true;
        }
      },
      hideBy: function hideBy(_ref2) {
        var context = _ref2.context;
        return hiddenIds.has(context.id);
      },
      nodeById: nodeById,
      onChangeHiddenIds: setHiddenIds,
      onDefaultAction: function onDefaultAction(options) {
        switch (variant === null || variant === void 0 ? void 0 : variant.type) {
          case "multiselect":
            variant.onDefaultAction(options);
            break;

          case "select":
            variant.onSelect(options);
            break;
        }

        if (onChange && options.event.key === KEYS.SPACE) {
          if (!a11yDragId) {
            if (canDrag(options)) {
              setA11yDragId(options.context.id);
            }
          } else {
            var source = {
              context: options.context.treeContext.getNodeContext(a11yDragId),
              node: nodeById[a11yDragId]
            };

            if (isTreeDropNodeValid({
              canDrop: canDrop,
              source: source,
              target: options
            })) {
              onChange({
                position: "childof",
                source: source,
                target: options
              });
              var nextHiddenIds = new Set(hiddenIds);

              if (options.context.isExpanded() || !options.context.hasChildren()) {
                nextHiddenIds.delete(source.context.id);
              } else {
                nextHiddenIds.add(source.context.id);
              }

              setHiddenIds(nextHiddenIds);
            }

            setA11yDragId(void 0);
          }
        }
      },
      orderBy: orderBy,
      renderNode: function renderNode(_ref3) {
        var context = _ref3.context,
            focused = _ref3.focused,
            node = _ref3.node;
        return /*#__PURE__*/jsx(TreeNode, {
          a11yDragId: a11yDragId,
          canOrder: canOrder,
          canDrag: canDrag,
          canDrop: canDrop,
          context: context,
          focused: focused,
          node: node,
          onExpand: function onExpand() {
            var nextHiddenIds = new Set(hiddenIds);
            context.getChildIds().map(function (childId) {
              return nextHiddenIds.delete(childId);
            });
            setHiddenIds(nextHiddenIds);

            if (_onExpand) {
              _onExpand({
                context: context,
                node: node
              });
            }
          },
          onChange: onChange,
          onCollapse: function onCollapse() {
            var nextHiddenIds = new Set(hiddenIds);
            context.traverseDepthFirst(function (descendantId) {
              return nextHiddenIds.add(descendantId);
            });
            setHiddenIds(nextHiddenIds);

            if (_onCollapse) {
              _onCollapse({
                context: context,
                node: node
              });
            }
          },
          onDragStart: onDragStart,
          renderButtons: renderButtons,
          renderIcon: renderIcon,
          renderLabel: renderLabel,
          variant: variant
        });
      }
    })]
  });
};

Tree.propTypes = {
  "canDrag": _PropTypes.func,
  "canDrop": _PropTypes.func,
  "canOrder": _PropTypes.bool,
  "filterBy": _PropTypes.func,
  "focusIndex": _PropTypes.number,
  "getInitialHiddenIds": _PropTypes.func,
  "nodeById": _PropTypes.object.isRequired,
  "onChange": _PropTypes.func,
  "onCollapse": _PropTypes.func,
  "onDragStart": _PropTypes.func,
  "onExpand": _PropTypes.func,
  "onFocusIndexChange": _PropTypes.func,
  "orderBy": _PropTypes.func,
  "renderButtons": _PropTypes.func,
  "renderIcon": _PropTypes.func,
  "renderLabel": _PropTypes.func.isRequired,
  "variant": _PropTypes.oneOfType([_PropTypes.shape({
    isSelected: _PropTypes.func,
    onSelect: _PropTypes.func,
    type: _PropTypes.oneOf(["select"])
  }), _PropTypes.shape({
    onDefaultAction: _PropTypes.func,
    renderCheckbox: _PropTypes.func,
    type: _PropTypes.oneOf(["multiselect"])
  })])
};
Tree.displayName = "Tree";

export { Tree, dnd };
